import type { LucideIcon } from 'lucide-react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { Popover as P } from 'radix-ui';
import { type KeyboardEvent, useId, useMemo, useRef, useState } from 'react';
import { cssVars, cx } from '@/lib/cx';
import { t, tp } from '@/lib/i18n';
import s from './Combobox.module.css';

export interface ComboOption {
  value: string;
  label: string;
  description?: string | null;
  group?: string;
  /** Data-driven colour (e.g. a group chip colour). */
  color?: string | null;
}

interface BaseProps {
  options: ComboOption[];
  placeholder: string;
  searchPlaceholder?: string;
  emptyText?: string;
  icon?: LucideIcon;
  size?: 'sm' | 'md';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  /** Called as the user types, for server-side search; options are still filtered locally. */
  onSearch?: (q: string) => void;
}

export interface MultiComboboxProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export interface SingleComboboxProps extends BaseProps {
  multiple?: false;
  value: string | null;
  onChange: (value: string | null) => void;
  clearable?: boolean;
}

/** Searchable single or multi select (projects, groups, vehicles…). */
export function Combobox(props: MultiComboboxProps | SingleComboboxProps) {
  const { options, placeholder, searchPlaceholder, emptyText, icon: Icon, size = 'md', disabled, loading, className, id, onSearch } = props;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = useMemo(() => new Set(props.multiple ? props.value : props.value ? [props.value] : []), [props.multiple, props.value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => `${o.label} ${o.description ?? ''} ${o.group ?? ''}`.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (value: string) => {
    if (props.multiple) {
      props.onChange(selected.has(value) ? props.value.filter((v) => v !== value) : [...props.value, value]);
    } else {
      props.onChange(value === props.value && props.clearable ? null : value);
      setOpen(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(0, Math.min(filtered.length - 1, active + (e.key === 'ArrowDown' ? 1 : -1)));
      setActive(next);
      listRef.current?.querySelector(`[data-index="${next}"]`)?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = filtered[active];
      if (o) toggle(o.value);
    }
  };

  const chosen = options.filter((o) => selected.has(o.value));
  const first = chosen[0];
  const label = !first
    ? placeholder
    : chosen.length === 1
      ? first.label
      : props.multiple
        ? tp(chosen.length, '{count} selected', '{count} selected')
        : first.label;

  const headers = filtered.map((o, i) => (o.group && o.group !== filtered[i - 1]?.group ? o.group : null));
  return (
    <P.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
        setActive(0);
      }}
    >
      <P.Trigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          className={cx(s.trigger, s[size], chosen.length > 0 && s.hasValue, className)}
          aria-label={props['aria-label']}
          aria-haspopup="listbox"
        >
          {Icon && <Icon className={s.leadIcon} aria-hidden />}
          {chosen.length === 1 && first?.color && <span className={s.dot} style={cssVars({ '--dot': first.color })} aria-hidden />}
          <span className={s.value}>{label}</span>
          <ChevronsUpDown className={s.chevron} aria-hidden />
        </button>
      </P.Trigger>
      <P.Portal>
        <P.Content
          className={s.content}
          sideOffset={4}
          align="start"
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            // Focus the search box ourselves, once the popover's focus scope is active: inside a
            // Dialog, an input's autoFocus fires while the dialog still traps focus, which sends it
            // back to the trigger — and typing to search did nothing (docs/bugs/2026-09-26-combobox-search-dead-in-dialogs.md).
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className={s.search}>
            <Search className={s.searchIcon} aria-hidden />
            <input
              ref={inputRef}
              className={s.searchInput}
              value={query}
              placeholder={searchPlaceholder ?? t('Search…')}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
                onSearch?.(e.target.value);
              }}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
            />
          </div>
          <div ref={listRef} id={listId} role="listbox" aria-multiselectable={props.multiple || undefined} className={s.list}>
            {loading && <div className={s.empty}>{t('Loading…')}</div>}
            {!loading && filtered.length === 0 && <div className={s.empty}>{emptyText ?? t('No matches')}</div>}
            {filtered.map((o, i) => {
              const header = headers[i];
              const isSel = selected.has(o.value);
              return (
                <div key={o.value}>
                  {header && <div className={s.group}>{header}</div>}
                  <div
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={isSel}
                    className={cx(s.option, i === active && s.active)}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => toggle(o.value)}
                  >
                    <span className={cx(s.check, props.multiple && s.box, isSel && s.checked)} aria-hidden>
                      {isSel && <Check />}
                    </span>
                    {o.color && <span className={s.dot} style={cssVars({ '--dot': o.color })} aria-hidden />}
                    <span className={s.optionText}>
                      <span className={s.optionLabel}>{o.label}</span>
                      {o.description && <span className={s.optionDescription}>{o.description}</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          {props.multiple && props.value.length > 0 && (
            <div className={s.footer}>
              <button type="button" className={s.clear} onClick={() => props.onChange([])}>
                <X aria-hidden /> {t('Clear selection')}
              </button>
            </div>
          )}
        </P.Content>
      </P.Portal>
    </P.Root>
  );
}
